/**
 * Google Map
 *
 * @module components/gmap
 */

import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import Component from './component';
import GMapApi from '../libs/gmap-api';
//import { getModule } from '../utils/modules';


export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  'use strict';

  // colors definition
  const colors = {
    primary: '#fd6e39'
  };

  // icons definition
  const icons = {
    marker: {
      path: 'M13.0909 0C9.62023 0.00393197 6.2929 1.32968 3.83877 3.68643C1.38464 6.04318 0.00410836 9.23849 1.39274e-05 12.5714C-0.00414289 15.2951 0.922303 17.9449 2.63723 20.1143C2.63723 20.1143 2.99426 20.5657 3.05257 20.6309L13.0909 32L23.134 20.6251C23.1863 20.5646 23.5446 20.1143 23.5446 20.1143L23.5457 20.1109C25.2598 17.9424 26.1858 15.2939 26.1818 12.5714C26.1777 9.23849 24.7972 6.04318 22.343 3.68643C19.8889 1.32968 16.5616 0.00393197 13.0909 0ZM13.0909 17.1429C12.1494 17.1429 11.229 16.8747 10.4462 16.3724C9.66337 15.8701 9.05323 15.1562 8.69293 14.3208C8.33263 13.4855 8.23837 12.5664 8.42204 11.6796C8.60572 10.7928 9.0591 9.97827 9.72484 9.33894C10.3906 8.69961 11.2388 8.26423 12.1622 8.08784C13.0856 7.91145 14.0428 8.00198 14.9126 8.34798C15.7824 8.69398 16.5259 9.27991 17.049 10.0317C17.572 10.7834 17.8512 11.6673 17.8512 12.5714C17.8496 13.7834 17.3476 14.9453 16.4552 15.8022C15.5628 16.6592 14.3529 17.1413 13.0909 17.1429Z',
      width: 27,
      height: 32
    }
  };

  // default map options
  const defaultMapOptions = {
    scrollwheel: false,
    controlSize: 28
  };

  /**
   * Retrive map options data.
   * 
   * @param {HTMLElement} el 
   * @returns {Object}
   */
  const getMapOptions = el => {
    // if we already retrieved before, use it.
    const curData = el.data('agg-map-options');
    if (curData) {
      return curData;
    }

    const id = el.attr('id');
    let opts = window[`aggMapOptions_${id}`] || {};

    // assuming we'd never use lat,lng = 0,0
    if ($.isPlainObject(opts.center)) {
      if (!opts.center.lat || !opts.center.lng || opts.center.lat === 0 || opts.center.lng === 0) {
        delete opts.center;
      }
    }

    opts = $.extend(true, {}, defaultMapOptions, opts);

    // save it as jQuery data
    el.data('agg-map-options', opts);
    return opts;
  };

  /**
   * Retrieve location entries.
   * 
   * @param {HTMLElement} el 
   * @returns 
   */
  const getMapLocationEntries = el => {
    // if we already retrieved before, use it.
    const curData = el.data('agg-map-locations');
    if (curData) {
      return curData;
    }

    const id = el.attr('id');
    const entries = window[`aggMapLocations_${id}`] || {};

    // save it as jQuery data
    el.data('agg-map-locations', entries);
    return entries;
  };

  /**
   * Cluster Renderer
   */
  class ClusterRenderer {
    constructor() {
      this.markerSVG = window.btoa(`
       <svg viewBox="0 0 32 32" width="32px" height="32px" xmlns="http://www.w3.org/2000/svg" fill="#54565a" fill-opacity="0.95">
         <path d="M 0 0 H 32 V 32 H 0 V 0 Z"></path>
       </svg>`);
    }

    render({ count, position }) {
      // create marker using svg icon
      return new google.maps.Marker({
        position,
        icon: {
          url: `data:image/svg+xml;base64,${this.markerSVG}`,
          scaledSize: new google.maps.Size(32, 32),
        },
        label: {
          text: String(count),
          color: 'rgba(255,255,255,0.9)',
          fontSize: '12px',
        },
        title: `Cluster of ${count} markers`,
        // adjust zIndex to be above other markers
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
      });
    }
  }

  /**
   * GMap component class
   */
  class GMap extends Component {
    /**
     * Constructor
     */
    constructor(element, options = {}) {
      super(element, options);
      this.name = 'gmap';
    }

    /**
     * Main initializer
     * 
     * @returns void
     */
    init() {
      const el = this.element;

      if (el.hasClass(this.initializedClass)) {
        // only need to initialize this component once
        return;
      }

      // load API if it's not loaded yet
      if (!GMap.apiReady) {
        GMap.loadApi()
          .then(() => {
            this.init();
          })
          .catch(err => {
            this.logError(err);
          });
        return;
      }

      // mark as initialized
      el.addClass(this.initializedClass);

      this.initMap();
    }

    /**
     * Init Map.
     */
    initMap() {
      const mapApi = GMap.mapApi;

      this.elMap = $(GMap.selectors.mapCanvas, this.element);

      if (!this.elMap.length) {
        return;
      }

      this.mapOptions = getMapOptions(this.element);
      this.locationEntries = getMapLocationEntries(this.element);

      // prepare icons
      this.iconMarker = {
        path: icons.marker.path,
        fillColor: colors.primary,
        fillOpacity: 1,
        anchor: new mapApi.Point(icons.marker.width / 2, icons.marker.height),
        scale: 1,
        strokeWeight: 0
      };

      // location bounds
      const bounds = new mapApi.LatLngBounds();

      // markers
      this.markers = this.locationEntries.map(entry => {
        var marker = new mapApi.Marker({
          position: entry.position,
          icon: this.iconMarker,
          //map: this.map,   // not needed since we are using MarkerCluster
          title: entry.title
        });

        // extend the bounds
        bounds.extend(entry.position);

        google.maps.event.addListener(marker, 'click', () => {
          this._onMarkerClick(marker, entry);
        });

        return marker;
      });

      // if the center is not defined, use the center point of the location entries bound
      if (!this.mapOptions.center) {
        this.mapOptions.center = bounds.getCenter();
      }

      // create map
      this.map = new mapApi.Map(this.elMap[0], this.mapOptions);

      // prepare info window
      this.infoWindow = new mapApi.InfoWindow({
        content: ''
      });

      this.markerCluster = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        algorithm: new SuperClusterAlgorithm({
          // maxZoom: 16
        }),
        renderer: new ClusterRenderer()
      });
    }

    /**
     * Create content for InfoWindow.
     * 
     * @param {Object} entry 
     * @returns {Object}
     */
    initEntryInfoWindowContent(entry) {
      let lines = [entry.address, entry.postcode];

      if (entry.tel) {
        lines.push([entry.telLabel, entry.tel].join(' '));
      }

      if (entry.hours) {
        lines.push([entry.hoursLabel, entry.hours].join(' '));
      }

      const tmp = $('<div>').append($('<p>').html(lines.join('<br>')));

      if (entry.gmapsLabel) {
        tmp.append(
          $('<p class="gmap--infowindow-cta">').append(
            $('<a>').attr({
              href: `https://www.google.com/maps/place/${entry.position.lat},${entry.position.lng}?hl=${entry.language}`,
              target: '_blank'
            }).text(entry.gmapsLabel)
          )
        );
      }

      entry._infoWindowContent = $('<div>').append(tmp).get(0);

      return entry;
    }

    /**
     * Pan and Zoom to the specific areas (array of cordinates)
     * 
     * @param {Object[]} data 
     */
    showArea(data) {
      const mapApi = GMap.mapApi;
      if (data && Array.isArray(data) && data.length) {
        // location bounds
        const bounds = new mapApi.LatLngBounds();

        data.forEach(coord => {
          // extend the bounds
          bounds.extend(coord);
        });

        this.map.fitBounds(bounds);
      }
      
    }

    /**
     * Handle Marker click.
     * 
     * @param {google.maps.Marker} marker 
     * @param {Object} entry 
     */
    _onMarkerClick(marker, entry) {
      this.infoWindow.close();

      if (!entry._infoWindowContent) {
        this.initEntryInfoWindowContent(entry);
      }

      this.infoWindow.setContent(entry._infoWindowContent);
      this.infoWindow.open(this.map, marker);
    }
  }

  GMap.selectors = {
    mapCanvas: '.gmap--map'
  };

  // APIs object
  GMap.mapApi = null;

  // API loader
  GMap.apiReady = false;
  GMap.loadApi = () => {
    if (GMap._loadingApi) {
      return GMap._loadingApi;
    }

    GMap._loadingApi = new Promise((resolve, reject) => {

      Promise.all([
        GMapApi.loadApi()
      ])
        .then(([mapApi]) => {
          GMap.apiReady = true;
          GMap.mapApi = mapApi;
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });

    return GMap._loadingApi;
  };

  return GMap;
})(jQuery, window);